/*
 * File Description: This file contains tools to manipulate data, columns and queries for tables
 * Usage :
 *    import TableTools from "@/utils/tableTools";
 *    TableTools.Helper.regroupDateAndTimeColumns(...)
 */

import i18n from "@/plugins/vue-i18n.js";
import Tools from "@/utils/tools";
import { numberFormat, formatAmount } from "@/filter/numberFormat";
import { dateFormat, dateFormatEnum } from "@/filter/dateFormat";
import moment from "moment";
import _ from "lodash";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import {
  columnTypeEnum,
  calculatedColumnsEnum,
  alternativeColumnLabelEnum,
} from "@/model/columnsEnums";
import { cubeInstance } from "./cubeJsInstance";
import { currencies } from "@/i18n";
import { getExportNumFormat } from "@/filter/exportFormat";

const helperManager = {
  /**
   * Retourne une copie du timeDimension dont le dateRange ne contient que la date (sans heure),
   * avec optionnellement une dimension remplacée.
   * Utile pour les queries sur des cubes journaliers qui ne supportent pas le filtre horaire.
   * @param {Object} timeDimension - l'objet timeDimension original (ex. { dimension: "...", dateRange: [...] })
   * @param {String} [dimension] - si fourni, remplace la propriété "dimension" du timeDimension
   * @returns {Object|null} copie du timeDimension avec dateRange tronqué, ou null si timeDimension est falsy
   */
  stripTimeDimension(timeDimension, dimension) {
    if (!timeDimension) return null;
    return {
      ...timeDimension,
      ...(dimension ? { dimension } : {}),
      dateRange: timeDimension.dateRange
        ? timeDimension.dateRange.map((d) => String(d).split("T")[0])
        : timeDimension.dateRange,
    };
  },

  /** Exécute une requête, et renvoie son résultat.
   * @query: requête cubeJs
   * @param {Object} [options] - options supplémentaires (ex. { signal: AbortSignal })
   * @returns: []
   */
  async doRequest(query, options = {}) {
    const res = await cubeInstance.load({ ...query, timezone: "UTC" }, options);
    return res.loadResponse.results[0].data;
  },

  /** Exécute une liste de requêtes, et renvoie leurs résultats.
   * @queries: [
   *  {
   *    groupKey: <identifiant unique de la requête, obligatoire si plusieurs requêtes renvoient la même colonne de bdd et s'il faut afficher un groupe>
   *    query: requête cubeJs
   *  },...
   * ]
   * @param {Object} [options] - options supplémentaires (ex. { signal: AbortSignal })
   * @returns: [
   *  {
   *    groupKey: <identifiant unique de la requête, reçu en paramètre>
   *    resultSet: <résultat de cubeJs>
   *  }
   * ]
   */
  async doMultipleRequest(queries, options = {}) {
    const resultSets = await Promise.all(
      queries.map((q) =>
        cubeInstance
          .load({ ...q.query, timezone: "UTC" }, options)
          .then((resultSet) => ({
            groupKey: q.groupKey,
            resultSet: resultSet,
          }))
      )
    );

    return resultSets;
  },

  /** Regroupe les résultats de plusieurs requêtes par les données communes indiquées,
   * en utilisant les groupKey pour différencier les mesures qui pourraient avoir la même clé (nom de colonne bdd)
   * @resultSets: [
   *  {
   *    groupKey: <identifiant unique de la requête, obligatoire si plusieurs requêtes renvoient la même colonne de bdd et s'il faut afficher un groupe>
   *    resultSet: <résultat de cubeJs>
   *  },...
   * ]
   * @dimensionsToUseForMerging: Array of String
   * @afterMergeOrder: {
   *   [dimensionEnum.DATE]: "asc",
   *   [dimensionEnum.KIOSK_NAME]: "asc"
   * }, sert à retrier les données quand elles proviennent de plusieurs requêtes
   * afin que ça ne se mélange pas tout lors de la fusion, inutile si le tableau
   * peut être de nouveau trié après
   * @returns: [
   *  {
   *    <colonne dimension 1>: <valeur>,
   *    <colonne dimension 2>: <valeur>,
   *    ...
   *    <colonne measure 1>-<group key 1>: <valeur>,
   *    <colonne measure 1>-<group key 2>: <valeur>,
   *    ...
   *    <colonne measure 2>-<group key 1>: <valeur>,
   *    <colonne measure 2>-<group key 2>: <valeur>,
   *    ...
   *    mergeKey: <concaténation des valeurs des colonnes dimension, pour faciliter le merge>
   *  },...
   * ]
   */
  mergeResultsByCommonDimensions(
    resultSets,
    dimensionsToUseForMerging,
    afterMergeOrder = {}
  ) {
    var concatenatedResults = [];
    var mergedResults = [];

    resultSets.forEach((set) => {
      set.resultSet.loadResponse.results[0].data.forEach((result) => {
        var updatedResult = set.groupKey
          ? this.renameWithGroupKey(
            result,
            dimensionsToUseForMerging,
            set.groupKey
          )
          : result;
        updatedResult.mergeKey = this.constructMergeKeyValue(
          result,
          dimensionsToUseForMerging
        );
        concatenatedResults.push(updatedResult);
      });
    });

    const mergeKeyValues = _.uniq(
      // re-trie les données si afterMergeOrder est défini
      _.orderBy(
        concatenatedResults,
        Object.keys(afterMergeOrder).map((key) => (v) => v[key].toLowerCase()),
        Object.values(afterMergeOrder)
      )
        // extrait seulement la mergeKey
        .map((a) => a.mergeKey)
    );
    mergeKeyValues.forEach((val) => {
      const resultsToMerge = concatenatedResults.filter(
        (r) => r.mergeKey === val
      );
      var mergedResult = {};
      for (let i = 0; i < resultsToMerge.length; i++) {
        mergedResult = Object.assign(resultsToMerge[i], mergedResult);
      }
      mergedResults.push(mergedResult);
    });

    return mergedResults;
  },

  renameWithGroupKey(obj, keysToNotRename, groupKey) {
    const keysToRename = _.difference(Object.keys(obj), keysToNotRename);
    keysToRename.forEach((oldKey) => {
      const newKey = this.formatKeyWithGroupKey(oldKey, groupKey);
      this.renameKey(obj, oldKey, newKey);
    });
    return obj;
  },

  renameKey(obj, oldKey, newKey) {
    if (obj[oldKey] !== undefined) {
      obj[newKey] = obj[oldKey];
      delete obj[oldKey];
    }
  },

  formatKeyWithGroupKey(baseKey, groupKey) {
    return `${baseKey}##${groupKey}`;
  },

  constructMergeKeyValue(obj, dimensionsToUseForMerging) {
    return dimensionsToUseForMerging
      .map((dimension) => obj[dimension])
      .join("##");
  },

  constructMeasureColumns(measures, groupKeys) {
    if (groupKeys && groupKeys.length) {
      var measureColumns = [];
      groupKeys.forEach((groupKey) => {
        var measuresByGroup = this.constructColumnTable(
          measures,
          groupKey,
          columnTypeEnum.MEASURE,
          true,
          null
        );
        measureColumns = measureColumns.concat(measuresByGroup);
      });
      return measureColumns;
    } else {
      return this.constructColumnTable(
        measures,
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );
    }
  },

  constructGroupHeaders(groupKeys, measureColumns) {
    var groupHeaders = [];

    groupKeys.forEach((key) => {
      const count = _.countBy(measureColumns, (col) => col.groupKey === key);
      groupHeaders = groupHeaders.concat(
        this.constructColumnTable(
          [key],
          null,
          columnTypeEnum.GROUP,
          null,
          count.true
        )
      );
    });

    return groupHeaders;
  },

  constructColumnTable(
    keys,
    groupKey,
    type,
    sortable,
    nbOfMeasures,
    displayZero = false,
    siteCurrency = "EUR"
  ) {
    var columnTable = keys.map((key) => {
      var fullKey = groupKey ? this.formatKeyWithGroupKey(key, groupKey) : key;

      var column = {
        key: fullKey,
        label: i18n.t(key).includes("{0}")
          ? i18n.t(key).replace("{0}", currencies[siteCurrency])
          : i18n.t(key),
        type: type,
        formatter: (value, key, item) => {
          // Lire la devise depuis la ligne au lieu d'utiliser siteCurrency fixe
          const rowCurrency =
            item && item["Site.currency"]
              ? item["Site.currency"]
              : siteCurrency;
          return TableTools.Helper.valueFormatter(
            value,
            key,
            displayZero,
            rowCurrency
          );
        },
        tdClass: (value, key, item) => {
          return TableTools.Helper.cellStyleFormatter(
            value,
            key,
            displayZero,
            item
          );
        },
        thAttr: (_value, key, _item) => {
          // ajout d'un tooltip sur le header si besoin
          return TableTools.Helper.addHeaderTooltip(key);
        },
      };
      if (groupKey) {
        column["groupKey"] = groupKey;
      }
      if (sortable !== undefined && sortable !== null)
        column["sortable"] = sortable;
      if (nbOfMeasures) column["nbOfMeasures"] = nbOfMeasures;

      return column;
    });
    return columnTable;
  },

  /**
   * Modifie la devise ('€', 'dh' etc...) présent dans un un header de table
   * En générale la devise se récupère après l'init des tables
   * Exemple :
   * par défaut :
   *      measureEnum.PURCHASEHISTORY_AMOUNT => Cartes vendues équivalent €
   * devient : Cartes vendues équivalent dh
   *
   * @param {Array<Object>} headerArray
   * @param {Array<String>} key
   * @param {String} siteCurrencyKey
   */
  updateCurrencyTableHeader(headerArray, keys, siteCurrencyKey) {
    return headerArray.map((header) => {
      if (keys.includes(header.key)) {
        return {
          ...header,
          label: this.replaceCurrencyInText(header.label, siteCurrencyKey),
        };
      }

      return header;
    });
  },

  replaceCurrencyInText(text, currencyKey) {
    const newCurrency = currencies[currencyKey];

    if (!newCurrency) {
      throw new Error("Devise invalide");
    }

    // On crée une regex qui match toutes les valeurs possibles de l'enum
    const pattern = new RegExp(
      Object.values(currencies)
        .map((symbol) => symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|"),
      "g"
    );

    return text.replace(pattern, newCurrency);
  },

  /** Construit avec les bons titres destinés aux graphiques  */
  constructGraphDensity(dataSeries, dataTitle) {
    return {
      dataSeries: dataSeries || [],
      dataTitle: dataTitle || "",
    };
  },

  /** Regroupe les différentes dimensions relatives à la date et l'heure en une seule dimension pour l'affichage
   * @dataTable: données du tableau ayant les colonnes DATE_DAY, DATE_MONTH, DATE_YEAR,
   * et éventuellement TIME_PARTOFDAY, TIME_HOUR, TIME_HALFHOUR, TIME_QUARTERHOUR
   * @hasTimeKeys: true si les données du tableau a des colonnes indiquant l'heure et qu'il faut l'intégrer dans la colonne concaténée
   * @newColumnKey: key à donner à la nouvelle colonne regroupée. ex : calculatedColumnsEnum.HOUR_PERIOD
   * @returns: dataTable modifiée, avec les données de la nouvelle colonne au format reconnu par les helpers valueFormatter / dateformat,
   * pour que l'affichage soit géré automatiquement comme pour les autres colonnes
   */
  regroupDateAndTimeColumns(dataTable, hasTimeKeys, newColumnKey) {
    dataTable.forEach((o) => {
      var day = o[dimensionEnum.DATE_DAY];
      var month = o[dimensionEnum.DATE_MONTH];
      var year = o[dimensionEnum.DATE_YEAR];
      var date;
      if (!hasTimeKeys) {
        date = moment(`${year}-${month}-${day}`, "YYYY-M-DD");
        o[newColumnKey] = date
          .format(dateFormatEnum.MOMENT_DATE_FORMAT)
          .toString();
      } else {
        var hour =
          o[dimensionEnum.TIME_PARTOFDAY] === undefined
            ? o[dimensionEnum.TIME_HOUR]
            : parseInt(o[dimensionEnum.TIME_PARTOFDAY], 10) * 12;
        var minutes =
          o[dimensionEnum.TIME_HALFHOUR] === undefined
            ? o[dimensionEnum.TIME_QUARTERHOUR] === undefined
              ? 0
              : o[dimensionEnum.TIME_QUARTERHOUR]
            : o[dimensionEnum.TIME_HALFHOUR];

        // NB : Si on doit afficher la date avec une notion d'heure, il faut afficher le jour réel (même si on n'affiche que l'heure, ça permet d'avoir le bon ordre)
        // Mais on n'a pas le realdatetime dans les résultats
        // => on "triche" en ajustant manuellement le jour si besoin en fonction de l'heure configurée pour le début d'un business day
        date = Tools.BusinessDateHelper.getRealDateFromBusinessDateAndTime(
          year,
          month,
          day,
          hour,
          minutes
        );
        o[newColumnKey] = date
          .format(dateFormatEnum.MOMENT_DATETIME_FORMAT)
          .toString(); // format reconnu par l'helper dateFormat
        delete o[dimensionEnum.TIME_HOUR];
        delete o[dimensionEnum.TIME_QUARTERHOUR];
        delete o[dimensionEnum.TIME_HALFHOUR];
        delete o[dimensionEnum.TIME_PARTOFDAY];
      }

      delete o[dimensionEnum.DATE_DAY];
      delete o[dimensionEnum.DATE_MONTH];
      delete o[dimensionEnum.DATE_YEAR];
    });
    return dataTable;
  },

  /** Renvoie l'objet contenant la somme de chaque colonne concernée. */
  calculateTotalsData(dataTable, concernedColumnsFullKeys) {
    var totalsData = {};
    concernedColumnsFullKeys.forEach((key) => {
      totalsData[key] = _.sumBy(dataTable, (data) => data[key] || 0);
    });
    return totalsData;
  },

  /** Renvoie l'objet contenant des lignes de total pour chaque groupe donné. */
  addGroupRows(
    dataTable,
    groupFullKeys,
    sumColumnsFullKeys,
    firstValueColumnsFullKeys,
    countColumnsFullKeys,
    isCustomGroups = false
  ) {
    if (dataTable && dataTable.length) {
      //calcul des totaux de groupes
      var totaux = groupBy(dataTable);

      //Ajout des totaux dans la table
      totaux.forEach((total) => {
        var iRow = dataTable.findIndex((r) => rowIsInGroup(r, total));
        if (iRow >= 0) {
          //Ajout du groupe
          dataTable.splice(iRow, 0, total);
        }
      });
    }
    // Filter out sub-rows when sorting by GAME_CODE
    if (groupFullKeys.includes(dimensionEnum.GAME_CODE)) {
      // Only hide sub-rows if sorting by GAME_CODE
      dataTable = dataTable.filter((row) => row.groupLevel === 0);
    }
    return dataTable;

    /** Fonction récursive retournant la liste des groupes avec le cumul des colonnes à sommer */
    function groupBy(rows, level) {
      level = level || 0;
      var resGroupBy = _.groupBy(rows, groupFullKeys[level]),
        res = [];

      Object.keys(resGroupBy).forEach((key) => {
        if (key !== "null") {
          // On ne retient pas les groupes sans titre
          //Groupe courant
          var sumGroupValue = sumGroup(resGroupBy[key], level);

          if (firstValueColumnsFullKeys) {
            // Add the first value columns
            firstValueColumnsFullKeys.forEach((col) => {
              sumGroupValue[col] = resGroupBy[key][0][col];
            });
          }

          if (countColumnsFullKeys) {
            // Add the count of rows in the group
            countColumnsFullKeys.forEach((col) => {
              sumGroupValue[col] = resGroupBy[key].length;
            });
          }

          res.push(sumGroupValue);
          if (level + 1 < groupFullKeys.length) {
            //On ajoute les sous-groupes
            res = res.concat(groupBy(resGroupBy[key], level + 1));
          }
        }
      });

      return res;
    }

    /** Calcul du cumul des colonnes à sommer pour le groupe concerné */
    function sumGroup(rows, level) {
      //Nom du groupe
      var res = {};
      res.groupLevel = level;
      res.customGroup = isCustomGroups;
      for (var iLevel = 0; iLevel <= level; iLevel++) {
        res[groupFullKeys[iLevel]] = rows[0][groupFullKeys[iLevel]];
      }
      //Somme des colonnes
      return rows.reduce((res, row) => {
        sumColumnsFullKeys.forEach(
          (key) => (res[key] = (res[key] || 0) + (row[key] || 0))
        );
        return res;
      }, res);
    }

    /** Vrai si la ligne correspond au groupe donné */
    function rowIsInGroup(row, group) {
      for (var iLevel = 0; iLevel <= group.groupLevel; iLevel++) {
        if (row[groupFullKeys[iLevel]] !== group[groupFullKeys[iLevel]]) {
          return false;
        }
      }
      return true;
    }
  },

  /** Renvoie l'objet contenant les valeurs moyennes pour chaque colonne concernée.
   * Attention : pas encore utilisée, probablement à ajuster pour tenir compte des lignes "manquantes" car sans valeur mais qui devraient être prises en compte pour une moyenne ?
   */
  calculateAveragesData(dataTable, concernedColumnsFullKeys) {
    var averagesData = {};
    concernedColumnsFullKeys.forEach((key) => {
      averagesData[key] = _.meanBy(dataTable, (data) => data[key] || 0);
    });
    return averagesData;
  },

  escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  },

  getKeyFromFullColmunKey(fullColumnKey) {
    return fullColumnKey
      .split("##")
      .find(
        (x) =>
          Object.values(dimensionEnum).includes(x) ||
          Object.values(mesureEnum).includes(x) ||
          Object.values(calculatedColumnsEnum).includes(x) ||
          Object.values(alternativeColumnLabelEnum).includes(x)
      );
  },

  valueFormatter(value, fullColumnKey, displayZero = false, siteCurrency) {
    const currency = siteCurrency || "EUR";
    const symbolCurrency = currencies[currency];
    if (typeof value == "boolean") {
      if (value) return i18n.t("Display.booltrue");
      else return i18n.t("Display.boolfalse");
    }
    const key = this.getKeyFromFullColmunKey(fullColumnKey);
    if (!isNaN(value)) return numberFormat(value, key, displayZero, currency);
    else if (this.isDate(value)) {
      return dateFormat(value, key);
    }

    const escapedSymbol = this.escapeRegExp(symbolCurrency);
    const regex = new RegExp(`^#([\\d${escapedSymbol}])#`);
    var getFormat = value && value.match(regex);
    if (getFormat) {
      value = parseFloat(value.substring(getFormat[0].length));
      //Le format est forcé pour la cellule
      if (getFormat[1] === symbolCurrency) {
        return formatAmount(value, displayZero, symbolCurrency);
      } else {
        //nombre de décimales attendues
        return value.toFixed(getFormat[1]);
      }
    }
    return value;
  },

  cellStyleFormatter(value, fullColumnKey, displayZero = false, item = null) {
    const alignRightClass = "col-align-right";
    let classList = [];

    const key = this.getKeyFromFullColmunKey(fullColumnKey);

    // Alignement à droite spécifique aux ligne de tableau
    if (
      item &&
      item[calculatedColumnsEnum.LABEL] ===
      i18n.t(calculatedColumnsEnum.MONETARY) &&
      key !== calculatedColumnsEnum.LABEL
    ) {
      classList.push(alignRightClass);
    }

    if (!isNaN(value) && typeof value == "number") {
      var formattedValue = numberFormat(value, key, displayZero);
      const symbols = Object.values(currencies);
      if (
        formattedValue != null &&
        symbols.some((symbol) => formattedValue.toString().includes(symbol))
      ) {
        classList.push(alignRightClass);
      }
    }
    return [...classList, ...this.cellSizeFormatter(key)];
  },

  /// return an array of class
  cellSizeFormatter(key) {
    switch (key) {
      case dimensionEnum.GAME_NAME:
      case dimensionEnum.GAMECATEGORY_NAME:
      case dimensionEnum.SMARTTOUCH_NUMBER:
      case dimensionEnum.OMNIPLAYEVENT_CUSTOMERNAME:
      case dimensionEnum.OMNIPLAYEVENT_REFERENCE:
      case dimensionEnum.OMNIPLAYEVENT_INVOICENUMBER:
        return ["col-ellipsis", "col-ellipsis-small"];
      case dimensionEnum.OMNIPLAYEVENT_EVENTTYPE:
        return ["col-ellipsis", "col-ellipsis-medium"];
      default:
        return [];
    }
  },

  addHeaderTooltip(fullColumnKey) {
    /**
     * si jamais une colonne a besoin d'un tooltip pour mieux expliquer les valeurs qu'elle contiennent
     * il suffit dans le fichier d'internationnalisation de rajouter la colonne dans la partie 'headerTooltips'
     * à chaque cellue de colonne du header, on va vérifier si un tooltip est présent dans le fichier d'internationnalisation
     * si oui on return sa value dans un objet
     * L'objet est ensuite traité dans TableRenderer
     */
    const dataAttributeName = "data-header-tooltip";
    const key = this.getKeyFromFullColmunKey(fullColumnKey);
    const path = `headerTooltips.${key}`;
    const isTooltipValue = i18n.te(path);
    let result = {};
    if (isTooltipValue) {
      result[dataAttributeName] = i18n.t(path);
    }

    return result;
  },

  formatAllValuesOfObject(obj, displayZero = false) {
    for (const keyobject of Object.keys(obj)) {
      obj[keyobject] = this.valueFormatter(
        obj[keyobject],
        keyobject,
        displayZero
      );
    }
    return obj;
  },

  formatDateLabel(dateArr) {
    let [start, end] = dateArr.map((d) => new Date(d));
    const sameYear = start.getFullYear() === end.getFullYear();
    start = moment(start, dateFormatEnum.MOMENT_DATE_FORMAT);
    end = moment(end, dateFormatEnum.MOMENT_DATE_FORMAT);

    const startString = sameYear
      ? start.format(dateFormatEnum.PERIOD_DATE_WITHOUT_YEAR_FORMAT).toString()
      : start.format(dateFormatEnum.PERIOD_DATE_WITH_YEAR_FORMAT).toString();
    const endString = end
      .format(dateFormatEnum.PERIOD_DATE_WITH_YEAR_FORMAT)
      .toString();

    return `${startString} - ${endString}`;
  },

  sortDatatableByDate(datatable, dateKey) {
    const compare = (a, b) => {
      const dateA = moment(a[dateKey]);
      const dateB = moment(b[dateKey]);
      return dateA - dateB;
    };
    return datatable.sort(compare);
  },

  sortDatatableByBusinessHour(datatable, hourKey) {
    const compare = (a, b) =>
      this.sortCompareBusinessHours(a[hourKey], b[hourKey]);

    return datatable.sort(compare);
  },

  sortCompareTableFields(a, b, key) {
    if (this.isDate(a[key]) && this.isDate(b[key])) {
      const dateA = moment(a[key]);
      const dateB = moment(b[key]);
      return dateA - dateB;
    } else if (key === calculatedColumnsEnum.HOUR_PERIOD) {
      return this.sortCompareBusinessHours(a[key], b[key]);
    } else {
      // Let b-table handle sorting other fields (other than `date` field)
      return false;
    }
  },

  sortCompareBusinessHours(hourA, hourB) {
    const orderA = Tools.BusinessDateHelper.getOrderFromBusinessHour(hourA);
    const orderB = Tools.BusinessDateHelper.getOrderFromBusinessHour(hourB);
    return orderA - orderB;
  },

  /** Indique approximativement si la valeur peut être reconnue comme une date par moment.js : respecte le pattern YYYY-MM-DD... */
  isDate(value) {
    return /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(value);
  },

  /** Construit l'objet attendu par l'export pour une table (à stocker ensuite avec storeExportData qui encapsulera la liste des tables) */
  assembleExportDataTable(
    dataTable,
    dimensionFields,
    measureFields,
    groupHeaders,
    tableHeader,
    defaultSortedColumn,
    averagesData,
    totalsData,
    tableNumber,
    customTableClass,
    topGroupHeaders
  ) {
    return {
      dataTable: dataTable || [],
      dimensionFields: dimensionFields || [],
      measureFields: measureFields || [],
      groupHeaders: groupHeaders || [],
      tableHeader: tableHeader || "",
      defaultSortedColumn: defaultSortedColumn || "",
      averagesData: averagesData || null,
      totalsData: totalsData || null,
      tableNumber: tableNumber || "",
      customTableClass: customTableClass || "",
      topGroupHeaders: topGroupHeaders || [],
    };
  },

  /** Construit l'objet attendu par l'export pour un graphe de densité (à stocker ensuite avec storeExportData) */
  assembleExportDataDensity(dataSeries, dataTitle, index) {
    return {
      dataSeries: dataSeries || [],
      dataTitle: dataTitle || "",
      chartNumber: index || "",
    };
  },

  storeExportData(
    exportDataTables,
    title,
    subtitles,
    filename,
    hideGroupsByTableNumber,
    reduceHeader,
    forcedTitleSubtitleColspan
  ) {
    const exportData = {
      tables: exportDataTables || [],
      title: title,
      subtitles: subtitles,
      filename: filename,
      hideGroupsByTableNumber: hideGroupsByTableNumber || null,
      reduceHeader: reduceHeader || false,
      forcedTitleSubtitleColspan: forcedTitleSubtitleColspan || null,
    };

    localStorage.exportData = JSON.stringify(exportData);
  },

  getFilterPurchaseHistoryNotBirthday() {
    return {
      dimension: dimensionEnum.PURCHASEHISTORY_ISBIRTHDAY,
      operator: "equals",
      values: ["False"],
    };
  },

  getFilterKioskNotDeleted() {
    return {
      dimension: dimensionEnum.KIOSK_ISDELETED,
      operator: "equals",
      values: ["False"],
    };
  },

  getFilterGameNotDeleted() {
    return {
      dimension: dimensionEnum.GAME_ISDELETED,
      operator: "equals",
      values: ["False"],
    };
  },

  /**
   * Filtre pour éviter les "jeux gratuits", ie. les smart touchs qui servent juste à entrer / sortir d'une zone
   * et sont représentés en BDD comme un jeu sans unités à dépenser
   * @returns {Object} : filtre attendu par CubeJs
   */
  getFilterGameNotFree() {
    return {
      or: [
        {
          dimension: mesureEnum.GAMEHISTORY_TOKEN,
          operator: "gt",
          values: ["0"],
        },
        {
          dimension: mesureEnum.GAMEHISTORY_BONUSTOKEN,
          operator: "gt",
          values: ["0"],
        },
      ],
    };
  },

  /**
   * Filtre pour le rapport comptable ignorer les données des jeux sans unités dépensées (payantes ou bonus)
   * Permet d'éviter les "jeux gratuits", ie. les smart touchs qui servent juste à entrer / sortir d'une zone
   * et sont représentés en BDD comme un jeu sans unités à dépenser
   * @param {boolean} isHourlyAccounting : indique qu'il s'agit du rapport comptable avec un filtre sur l'horaire
   * @returns {Object} : filtre attendu par CubeJs
   */
  getFilterAccountingDataWithTokens(isHourlyAccounting) {
    return {
      dimension: dimensionEnum.VFULLHOURLYACCOUNTING_ISFREEGAME,
      operator: "equals",
      values: ["0"],
    };
  },

  getFilterSmarttouchNotDeleted() {
    return {
      dimension: dimensionEnum.SMARTTOUCH_ISDELETED,
      operator: "equals",
      values: ["False"],
    };
  },
};

/** Instanciation of all helpers */

const TableTools = { Helper: { ...helperManager } };
export default TableTools;
